import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { productsService } from '../services';
import type { Product, ProductContextType, ProductInput } from '../types';

const ProductContext = createContext<ProductContextType | null>(null);

export function ProductProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshProducts = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const nextProducts = await productsService.fetchProducts();
      setProducts(nextProducts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ürünler yüklenemedi.');
    } finally {
      if (!options?.silent) {
        setIsLoading(false);
      }
    }
  }, []);

  const getProductById = useCallback(
    (id: string) => products.find((p) => p.id === id),
    [products],
  );

  const loadProduct = useCallback(async (id: string): Promise<Product | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const product = await productsService.getProduct(id);
      setProducts((prev) => {
        if (prev.some((item) => item.id === id)) {
          return prev.map((item) => (item.id === id ? product : item));
        }
        return [...prev, product];
      });
      return product;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ürün yüklenemedi.');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createProduct = async (input: ProductInput) => {
    setError(null);
    const product = await productsService.createProduct(input);
    setProducts((prev) => [...prev, product]);
    return product;
  };

  const updateProduct = async (id: string, input: ProductInput) => {
    setError(null);
    const product = await productsService.updateProduct(id, input);
    setProducts((prev) => prev.map((p) => (p.id === id ? product : p)));
    return product;
  };

  const deleteProduct = async (id: string) => {
    setError(null);
    await productsService.deleteProduct(id);
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  const uploadProductImages = async (id: string, files: File[]) => {
    setError(null);
    const product = await productsService.uploadProductImages(id, files);
    setProducts((prev) => prev.map((p) => (p.id === id ? product : p)));
    return product;
  };

  const deleteProductImage = async (id: string, url: string) => {
    setError(null);
    const product = await productsService.deleteProductImage(id, url);
    setProducts((prev) => prev.map((p) => (p.id === id ? product : p)));
    return product;
  };

  const value = useMemo(
    () => ({
      products,
      isLoading,
      error,
      refreshProducts,
      loadProduct,
      getProductById,
      createProduct,
      updateProduct,
      deleteProduct,
      uploadProductImages,
      deleteProductImage,
    }),
    [products, isLoading, error, refreshProducts, loadProduct, getProductById],
  );

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
}

export function useProducts() {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within ProductProvider');
  }
  return context;
}
